import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollText, Trash2, CheckCircle2, Unlock, UserCog, Settings, CreditCard } from "lucide-react";
import { useLang } from "@/i18n";

const ICON = { delete: Trash2, mark_paid: CheckCircle2, reopen: Unlock, staff: UserCog, settings: Settings, terminal: CreditCard };
const TONE = { delete: "bg-rose-500/15 text-rose-700 border-rose-500/30", mark_paid: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  reopen: "bg-amber-500/15 text-amber-700 border-amber-500/30", staff: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  settings: "bg-slate-500/15 text-slate-700 border-slate-500/30", terminal: "bg-primary/15 text-primary border-primary/30" };
const FILTERS = ["", "invoice", "repair", "customer", "inventory", "staff", "settings"];

function kindOf(action) {
  if (action.endsWith(".delete")) return "delete";
  if (action.endsWith(".mark_paid")) return "mark_paid";
  if (action.endsWith(".reopen")) return "reopen";
  if (action.startsWith("staff")) return "staff";
  if (action.startsWith("settings")) return "settings";
  return "terminal";
}

export default function AuditLog() {
  const { t } = useLang();
  const [filter, setFilter] = useState("");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["audit-logs", filter],
    queryFn: () => api.get("/audit-logs", { params: filter ? { action: filter } : {} }).then(r => r.data),
  });
  const fmt = (iso) => new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const detail = (r) => [r.label, r.meta?.amount != null ? `€ ${Number(r.meta.amount).toFixed(2)}` : null, r.meta?.method, r.meta?.role,
    r.meta?.card_number, r.meta?.changed?.length ? r.meta.changed.join(", ") : null].filter(Boolean).join(" · ");

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">{t("owner")}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">{t("auditLog")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("auditLogSub")}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`audit-filter-${f || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
            {f ? t(`audit_${f}`) : t("all")}
          </button>
        ))}
      </div>
      <Card className="divide-y divide-border border-border">
        {isLoading && <div className="p-8 text-center text-sm text-muted-foreground">…</div>}
        {!isLoading && rows.length === 0 && <div className="p-12 text-center text-muted-foreground text-sm"><ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" />{t("auditEmpty")}</div>}
        {rows.map(r => {
          const k = kindOf(r.action); const Icon = ICON[k];
          return (
            <div key={r.id} className="flex items-start gap-4 p-4" data-testid={`audit-row-${r.id}`}>
              <div className={`h-8 w-8 rounded-full border flex items-center justify-center shrink-0 ${TONE[k]}`}><Icon className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{t(`audit_action_${r.action.replace(".", "_")}`) || r.action}</span>
                  <Badge variant="outline" className="font-mono text-[10px]">{r.target_type}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{detail(r) || r.target_id}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-medium">{r.user_name || r.user_email}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{fmt(r.at)}</div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
